/* eslint-disable no-nested-ternary */
import { FC, useEffect, useState } from 'react'
import { Collapse, Form, Spin } from 'antd'
import useSingleBroadcast from 'hooks/useSingleBroadcast'
import PanelHeader from 'component/Broadcast/BroadcastPanels/PanelHeader'
import BroadcastFormTemplate from 'component/Broadcast/BroadcastPanels/BroadcastFormTemplate'
import {
	BroadcastContactAttachment,
	QuickReplyIntent,
	Template,
	UpdateBroadcastBody,
} from 'types'
import moment, { Moment } from 'moment'
import emptyMessage from 'data/emptyMessage'
import BroadcastHiddenForm from 'component/Broadcast/BroadcastHiddenForm'
import BroadcastTimeSensitive from 'component/Broadcast/BroadcastPanels/BroadcastTimeSensitive'
import BroadcastFormAudience from 'component/Broadcast/BroadcastPanels/BroadcastFormAudience'
import BroadcastFormParameters from 'component/Broadcast/BroadcastPanels/BroadcastFormParameters'
import BroadcastFormReview from 'component/Broadcast/BroadcastPanels/BroadcastFormReview'
import BroadcastFormSchedule from 'component/Broadcast/BroadcastPanels/BroadcastFormSchedule'

import BroadcastFormSettings from 'component/Broadcast/BroadcastPanels/BroadcastFormSettings'
import BroadcastLegacyFormSettings from 'component/Broadcast/BroadcastPanels/BroadcastLegacyFormSettings'

import BroadcastFormEstimates from 'component/Broadcast/BroadcastPanels/BroadcastFormEstimates'
import styles from './styles.module.scss'

const { Panel } = Collapse

export interface Parameter {
	number: number
	type: null | 'CONTACT' | 'CSV' | 'FIXED' | 'UNIQUE'
	contactField: null | string
	csvField: null | string
	value: string
}

export interface BroadcastFormValues
	extends Omit<UpdateBroadcastBody, 'time' | 'isTimeSensitive'> {
	templatePreview: string
	templateReference: null | Template
	autoAssignType: 'TEAM' | 'AGENT' | null
	time: Moment | null
	audienceTypeFilter: null | string | string[]
	parameters: Parameter[]
	templateHeaderParameter: Parameter[]
	templateUrlParameter: Parameter[]
	scheduleDate: Moment | null
	scheduleTime: Moment | null
	isTimeSensitive: 'BATCH' | 'INSTANT'
	staticAttachment: BroadcastContactAttachment | null
	qrBtnActionSetting: QuickReplyIntent[]
}

const BroadcastDetails: FC = () => {
	const [form] = Form.useForm<BroadcastFormValues>()

	const { broadcast, loading } = useSingleBroadcast()

	const currentStep = broadcast?.currentStep || 1

	const [activeKey, setActiveKey] = useState(1)

	const handleCollapseChange = (key: string | string[]) => {
		if (typeof key === 'string') {
			setActiveKey(parseInt(key, 10))
		}
	}

	useEffect(() => {
		if (broadcast !== null) {
			// ! We should be getting template back from the server
			const template = broadcast.template as Template | null

			const parameters = []
			const templateHeaderParameter = []
			const templateUrlParameter = []

			if (template !== null) {
				const curlyExpression = /{{([^(})]+)}/g
				let match
				// eslint-disable-next-line no-cond-assign
				while ((match = curlyExpression.exec(template.formattedTemplate))) {
					const index = Number(match[1]) - 1

					parameters.push({
						number: Number(match[1]),
						type: template.parameterSelection?.[index]?.selection?.includes(
							'contact_value'
						)
							? ('CONTACT' as 'CONTACT')
							: template.parameterSelection?.[index]?.selection === 'fixed_value'
							? ('FIXED' as 'FIXED')
							: null,
						contactField: template?.parameterSelection?.[index]?.selection?.includes(
							'contact_value'
						)
							? template.parameterSelection?.[index]?.selection || null
							: null,
						value: template.parameterSelection?.[index]?.fallback || '',
					})
				}

				if ((template.header?.params.length || 0) > 0) {
					templateHeaderParameter.push({
						number: 1,
						type: template.headerSelection?.[0]?.selection?.includes('contact_value')
							? ('CONTACT' as 'CONTACT')
							: template.headerSelection?.[0]?.selection === 'fixed_value'
							? ('FIXED' as 'FIXED')
							: null,
						contactField: template?.headerSelection?.[0]?.selection?.includes(
							'contact_value'
						)
							? template.headerSelection?.[0]?.selection || null
							: null,
						value: template.headerSelection?.[0]?.fallback || '',
					})
				}

				if (template.is_dynamic_btn_url) {
					templateUrlParameter.push({
						number: 1,
						type: template.urlParameterSelection?.[0]?.selection?.includes(
							'contact_value'
						)
							? ('CONTACT' as 'CONTACT')
							: template.urlParameterSelection?.[0]?.selection === 'fixed_value'
							? ('FIXED' as 'FIXED')
							: null,
						contactField: template?.urlParameterSelection?.[0]?.selection?.includes(
							'contact_value'
						)
							? template.urlParameterSelection?.[0]?.selection || null
							: null,
						value: template.urlParameterSelection?.[0]?.fallback || '',
					})
				}
			}

			form.setFieldsValue({
				template: template?._id || null,
				templateReference: template,
				templatePreview: template?.formattedTemplate || '',
				audienceType: broadcast.audienceType,
				audienceTypeFilter:
					broadcast.audienceType === 'ALL_OPTED_IN'
						? null
						: broadcast.audienceType === 'TAG_SELECTED'
						? broadcast.query_payload?.tag?.split(';') || null
						: broadcast.audienceType === 'VIEW_SELECTED'
						? broadcast.query_payload?.view || null
						: broadcast.audienceType === 'IMPORTED_FILE'
						? broadcast.query_payload?.file || null
						: broadcast.audienceType === 'INTELLIGENT_AUDIENCE'
						? broadcast.query_payload?.selection || null
						: null,
				parameters,
				templateHeaderParameter,
				templateUrlParameter,
				mediaSelectionType: broadcast.mediaSelectionType,
				currentStep: broadcast.currentStep || 1,
				time: broadcast.time ? moment(broadcast.time) : null,
				scheduleDate: broadcast.time ? moment(broadcast.time) : null,
				scheduleTime: broadcast.time ? moment(broadcast.time) : null,
				autoAssignType:
					broadcast.autoAssignTeam !== null
						? 'TEAM'
						: broadcast.autoAssignAgents.length !== 0
						? 'AGENT'
						: null,
				autoAssignTeam: broadcast.autoAssignTeam,
				autoAssignAgents: broadcast.autoAssignAgents,
				tags: broadcast.tags,
				enquiry: broadcast.enquiry,
				defaultMessage: broadcast.defaultMessage || emptyMessage,
				shouldSchedule: false,
				isTimeSensitive: broadcast.isTimeSensitive ? 'INSTANT' : 'BATCH',
				qrBtnActionSetting: broadcast.replyActionIntentSetting || [],
			})
		}
	}, [broadcast, form])

	const isTimeSensitiveNext = async () => {
		form.setFieldsValue({
			currentStep: 3,
		})
	}

	return (
		<div>
			<Spin spinning={loading} tip='Loading Broadcast'>
				<Form
					form={form}
					name='broadcastForm'
					layout='vertical'
					requiredMark={false}
					initialValues={{
						templateParameter: [],
						headerParameter: [],
						urlParameter: [],
						audienceType: null,
						isTimeSensitive: false,
						time: null,
						template: null,
						currentStep: 1,
						mediaSelectionType: null,
						media_url: null,
						fileName: null,
						shouldSchedule: false,
						autoAssignTeam: null,
						autoAssignAgents: [],
						enquiry: false,
						tags: [],
						templatePreview: '',
						templateReference: null,
						defaultMessage: emptyMessage,
						audienceTypeFilter: null,
						parameters: [],
						templateHeaderParameter: [],
						templateUrlParameter: [],
						scheduleDate: null,
						scheduleTime: null,
						qrBtnActionSetting: [],
					}}
				>
					{/* THIS FORM HELP KEEP STATE IN FORM INSTANCE */}
					<BroadcastHiddenForm />
					<Collapse
						className={styles.collapse}
						activeKey={activeKey}
						onChange={handleCollapseChange}
						ghost
						accordion
						expandIconPosition='right'
						destroyInactivePanel
					>
						<Panel
							collapsible={currentStep < 1 ? 'disabled' : undefined}
							header={
								<PanelHeader activeKey={activeKey} title='Template' panelKey={1} />
							}
							key={1}
						>
							<BroadcastFormTemplate canEditBroadcast={false} />
						</Panel>
						<Panel
							collapsible={currentStep < 2 ? 'disabled' : undefined}
							header={
								<PanelHeader
									activeKey={activeKey}
									title='Time Sensitive'
									panelKey={2}
								/>
							}
							key={2}
						>
							<BroadcastTimeSensitive
								onSuccess={isTimeSensitiveNext}
								canEditBroadcast={false}
							/>
						</Panel>
						<Panel
							collapsible={currentStep < 3 ? 'disabled' : undefined}
							header={
								<PanelHeader activeKey={activeKey} title='Audience' panelKey={3} />
							}
							key={3}
						>
							<BroadcastFormAudience form={form} canEditBroadcast={false} />
						</Panel>
						<Panel
							collapsible={currentStep < 4 ? 'disabled' : undefined}
							header={
								<PanelHeader
									activeKey={activeKey}
									title='Parameters and Media'
									panelKey={4}
								/>
							}
							key={4}
						>
							<BroadcastFormParameters form={form} canEditBroadcast={false} />
						</Panel>
						<Panel
							collapsible={currentStep < 5 ? 'disabled' : undefined}
							header={
								<PanelHeader activeKey={activeKey} title='Review' panelKey={5} />
							}
							key={5}
						>
							<BroadcastFormReview form={form} canEditBroadcast={false} />
						</Panel>
						<Panel
							collapsible={currentStep < 6 ? 'disabled' : undefined}
							header={
								<PanelHeader activeKey={activeKey} title='Schedule' panelKey={6} />
							}
							key={6}
						>
							<BroadcastFormSchedule form={form} canEditBroadcast={false} />
						</Panel>
						<Panel
							collapsible={currentStep < 7 ? 'disabled' : undefined}
							header={
								<PanelHeader
									activeKey={activeKey}
									title='Broadcast Campaign Settings'
									panelKey={7}
								/>
							}
							key={7}
						>
							{broadcast?.replySetting === 'ACTION' ? (
								<BroadcastFormSettings form={form} />
							) : (
								<BroadcastLegacyFormSettings form={form} canEditBroadcast={false} />
							)}
						</Panel>
						<Panel
							collapsible={currentStep < 8 ? 'disabled' : undefined}
							header={
								<PanelHeader activeKey={activeKey} title='Estimates' panelKey={8} />
							}
							key={8}
						>
							<BroadcastFormEstimates />
						</Panel>
					</Collapse>
				</Form>
			</Spin>
		</div>
	)
}

export default BroadcastDetails
